/**
 * Detect which Office host loaded us and wait for Office.js to be ready.
 *
 * This module is the only place that should reference the global `Office`
 * object during bootstrap; everything else uses the `OfficeHost` enum.
 */

export type OfficeHost = 'Word' | 'Excel' | 'PowerPoint' | 'Outlook' | 'Teams' | 'Meet' | 'Unknown';

export interface OfficeReadyInfo {
  host: OfficeHost;
  /** True when Office.js is present (i.e. running inside an Office host). */
  inOffice: boolean;
}

/**
 * If the page was loaded with `?host=teams` or `?host=meet`, we skip
 * Office.js entirely and mark the host accordingly. The Teams personal tab
 * and the Google Meet web add-on both set this query param so we can render
 * a document-free chat surface.
 */
function hostFromUrl(): OfficeHost | null {
  try {
    const p = new URLSearchParams(window.location.search);
    const h = p.get('host');
    if (h === 'teams') return 'Teams';
    if (h === 'meet') return 'Meet';
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Wait for Office.onReady. If we're running outside Office (browser preview
 * or Teams tab), resolve immediately with `inOffice: false` so the dev
 * experience still works.
 */
export function waitForOffice(timeoutMs = 4000): Promise<OfficeReadyInfo> {
  return new Promise((resolve) => {
    const urlHost = hostFromUrl();
    if (urlHost) {
      resolve({ host: urlHost, inOffice: false });
      return;
    }
    if (typeof Office === 'undefined' || !Office?.onReady) {
      resolve({ host: 'Unknown', inOffice: false });
      return;
    }
    const t = window.setTimeout(() => {
      resolve({ host: 'Unknown', inOffice: false });
    }, timeoutMs);

    Office.onReady((info) => {
      window.clearTimeout(t);
      const host = mapHost(info?.host);
      resolve({ host, inOffice: host !== 'Unknown' });
    });
  });
}

function mapHost(host: Office.HostType | undefined): OfficeHost {
  switch (host) {
    case Office.HostType.Word:
      return 'Word';
    case Office.HostType.Excel:
      return 'Excel';
    case Office.HostType.PowerPoint:
      return 'PowerPoint';
    case Office.HostType.Outlook:
      return 'Outlook';
    default:
      return 'Unknown';
  }
}
